/**
 * The operator's corrections in the badge window. The window only collects them; saving and
 * printing stay with the caller, so the badge has a single source.
 */
export interface ScanEdits {
  /** The company for the badge; empty means the badge has no company line. */
  company: string;
  lastName: string;
  firstName: string;
}

/** The ticket fields a desk correction writes; only the ones that moved are present. */
export interface TicketCorrection {
  organizationName?: string;
  lastName?: string;
  firstName?: string;
}

/** Whitespace is not an edit: a stray trailing space neither marks the field nor gets saved. */
export const fieldChanged = (value: string, original: string): boolean =>
  value.trim() !== original.trim();

/**
 * The one rule for what a correction changes. The window enables Save with it and the page
 * sends it, so an enabled Save always has something to send.
 */
export function scanEditChanges(original: ScanEdits, edits: ScanEdits): TicketCorrection {
  const changes: TicketCorrection = {};
  // The company goes to the ticket's typed organization, the badge's first source; the linked
  // Company record is the billing identity and a desk correction must not overwrite it.
  if (fieldChanged(edits.company, original.company)) {
    changes.organizationName = edits.company.trim();
  }
  if (fieldChanged(edits.lastName, original.lastName)) {
    changes.lastName = edits.lastName.trim();
  }
  if (fieldChanged(edits.firstName, original.firstName)) {
    changes.firstName = edits.firstName.trim();
  }
  return changes;
}

export const hasChanges = (changes: TicketCorrection): boolean => Object.keys(changes).length > 0;
